import React, { useState, useEffect, useRef, useCallback } from 'react';
import api from '../services/api';
import PasswordInput from './PasswordInput';

function ExportModal({ token, apiUrl, onClose }) {
  const [phase, setPhase] = useState('loading'); // loading, configure, generating, complete, error
  const [estimate, setEstimate] = useState(null);
  const [includeMedia, setIncludeMedia] = useState(true);
  const [password, setPassword] = useState('');
  const [jobId, setJobId] = useState(null);
  const [progress, setProgress] = useState(0);
  const [processedItems, setProcessedItems] = useState(0);
  const [totalItems, setTotalItems] = useState(0);
  const [errorMessage, setErrorMessage] = useState('');
  const eventSourceRef = useRef(null);

  const cleanupSSE = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  // Fetch estimate on mount
  useEffect(() => {
    let cancelled = false;

    async function fetchEstimate() {
      try {
        const res = await api.get('/export/estimate');
        if (!cancelled) {
          setEstimate(res.data);
          if (res.data.reflectionCount === 0) {
            setPhase('configure');
          } else {
            setPhase('configure');
          }
        }
      } catch (err) {
        if (!cancelled) {
          setErrorMessage(err.response?.data?.error || 'Failed to load export info');
          setPhase('error');
        }
      }
    }

    fetchEstimate();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup SSE on unmount
  useEffect(() => {
    return cleanupSSE;
  }, [cleanupSSE]);

  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1048576) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1073741824) return `${(bytes / 1048576).toFixed(1)} MB`;
    return `${(bytes / 1073741824).toFixed(1)} GB`;
  };

  const handleStartExport = async () => {
    try {
      setPhase('generating');
      setProgress(0);
      setProcessedItems(0);

      const res = await api.post('/export/start', { includeMedia, password });
      setPassword('');
      const newJobId = res.data.jobId;
      setJobId(newJobId);
      setTotalItems(estimate.reflectionCount);

      // Connect SSE for progress
      const es = new EventSource(`${apiUrl}/export/status/${newJobId}?token=${token}`);
      eventSourceRef.current = es;

      es.onmessage = (event) => {
        const data = JSON.parse(event.data);
        setProgress(data.progress);
        setProcessedItems(data.processedItems);
        setTotalItems(data.totalItems);

        if (data.status === 'complete') {
          cleanupSSE();
          triggerDownload(newJobId);
        } else if (data.status === 'error') {
          cleanupSSE();
          setErrorMessage(data.errorMessage || 'Export failed');
          setPhase('error');
        } else if (data.status === 'cancelled') {
          cleanupSSE();
          onClose();
        }
      };

      es.onerror = () => {
        // SSE connection lost - try to reconnect once or show error
        cleanupSSE();
        // Don't error immediately; the job continues on server
      };
    } catch (err) {
      const errMsg = err.response?.data?.error || 'Failed to start export';
      setPassword('');
      if (err.response?.status === 401) {
        // Wrong password — stay on configure phase so user can retry
        setErrorMessage(errMsg);
        setPhase('configure');
      } else {
        setErrorMessage(errMsg);
        setPhase('error');
      }
    }
  };

  const triggerDownload = async (downloadJobId) => {
    try {
      const res = await api.get(`/export/download/${downloadJobId}`, {
        responseType: 'blob'
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `brings-to-mind-export-${dateStr}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      setPhase('complete');
    } catch (err) {
      setErrorMessage('Failed to download export file');
      setPhase('error');
    }
  };

  const handleCancel = async () => {
    if (jobId) {
      try {
        await api.post(`/export/cancel/${jobId}`, {});
      } catch (e) {
        // Ignore cancel errors
      }
    }
    cleanupSSE();
    onClose();
  };

  const handleRetry = () => {
    setErrorMessage('');
    setPhase('configure');
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: '480px', width: '90%' }}>
        <div className="modal-header" style={{ marginBottom: '1rem' }}>
          <h3 style={{ margin: 0 }}>Export Reflections</h3>
        </div>

        {phase === 'loading' && (
          <div style={{ textAlign: 'center', padding: '2rem 0' }}>
            <p>Loading export information...</p>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '1rem' }}>
              <button onClick={onClose} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {phase === 'configure' && estimate && (
          <div>
            {estimate.reflectionCount === 0 ? (
              <div style={{ textAlign: 'center', padding: '1rem 0' }}>
                <p>No reflections to export.</p>
                <button onClick={onClose} style={{ marginTop: '1rem', padding: '0.5rem 1.5rem', cursor: 'pointer' }}>Close</button>
              </div>
            ) : (
              <>
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ margin: '0.3rem 0' }}><strong>{estimate.reflectionCount}</strong> reflections</p>
                  <p style={{ margin: '0.3rem 0' }}><strong>{estimate.mediaCount}</strong> media files</p>
                  <p style={{ margin: '0.3rem 0', color: 'var(--color-text-secondary, #666)' }}>
                    Estimated size: <strong>{formatSize(includeMedia ? estimate.estimatedSizeWithMedia : estimate.estimatedSizeTextOnly)}</strong>
                  </p>
                </div>

                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={includeMedia}
                      onChange={(e) => setIncludeMedia(e.target.checked)}
                    />
                    Include media files (video/audio)
                  </label>
                </div>

                <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary, #666)', margin: '0 0 1rem' }}>
                  Export includes an HTML file for offline browsing, a Word document, {includeMedia ? 'and all media files.' : 'without media files.'}
                </p>

                <div style={{ marginBottom: '1rem' }}>
                  <label style={{ display: 'block', marginBottom: '0.3rem', fontSize: '0.9rem' }}>Confirm your password to export</label>
                  <PasswordInput
                    value={password}
                    onChange={(e) => { setPassword(e.target.value); setErrorMessage(''); }}
                    placeholder="Enter your password"
                    required
                  />
                  {errorMessage && (
                    <p style={{ color: 'var(--color-error, #dc3545)', fontSize: '0.85rem', margin: '0.3rem 0 0' }}>{errorMessage}</p>
                  )}
                </div>

                <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                  <button onClick={onClose} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
                  <button
                    onClick={handleStartExport}
                    disabled={!password}
                    style={{
                      padding: '0.5rem 1.5rem',
                      background: password ? 'var(--color-primary, #2c5530)' : '#ccc',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: password ? 'pointer' : 'not-allowed',
                      fontWeight: 'bold'
                    }}
                  >
                    Export
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {phase === 'generating' && (
          <div style={{ padding: '1rem 0' }}>
            <p style={{ marginBottom: '0.5rem' }}>Generating export...</p>
            <div style={{
              width: '100%',
              height: '20px',
              background: 'var(--color-bg-secondary, #e0e0e0)',
              borderRadius: '10px',
              overflow: 'hidden',
              marginBottom: '0.5rem'
            }}>
              <div style={{
                width: `${progress}%`,
                height: '100%',
                background: 'var(--color-primary, #2c5530)',
                transition: 'width 0.3s ease',
                borderRadius: '10px'
              }} />
            </div>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-secondary, #666)', margin: '0 0 1rem' }}>
              Processing item {processedItems} of {totalItems} ({progress}%)
            </p>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <button onClick={handleCancel} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>Cancel</button>
            </div>
          </div>
        )}

        {phase === 'complete' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Export complete! Download started.</p>
            <button onClick={onClose} style={{
              padding: '0.5rem 1.5rem',
              background: 'var(--color-primary, #2c5530)',
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}>Close</button>
          </div>
        )}

        {phase === 'error' && (
          <div style={{ textAlign: 'center', padding: '1rem 0' }}>
            <p style={{ color: 'var(--color-error, #dc3545)', marginBottom: '1rem' }}>{errorMessage}</p>
            <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center' }}>
              <button onClick={handleRetry} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>Retry</button>
              <button onClick={onClose} style={{ padding: '0.5rem 1rem', cursor: 'pointer' }}>Close</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default ExportModal;
